<template>
  <div
    v-if="settings.widget_enabled"
    id="dacb-frame"
    :class="{
      '-connection-error': connectionError,
      '-safari-ios': safariIOS,
      '-input-focused': inputFocused,
      '-mobile': mobileClassEnabled,
      '-open': windowOpen
    }"
  >
    <span class="dacb-badge" :style="{ backgroundColor: options.primaryColour }" title="Chat" @click="open">
      <svg
        viewBox="0 0 35 31"
        version="1.1"
        xmlns="http://www.w3.org/2000/svg"
        xmlns:xlink="http://www.w3.org/1999/xlink"
      >
        <g id="Symbols" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
          <g id="Branding" transform="translate(-22.000000, -27.000000)" fill-rule="nonzero">
            <g id="logo" transform="translate(22.488281, 27.659990)">
              <path
                id="Shape"
                d="M22.9022301,6.33746855 C23.1459789,6.33746855 23.3897277,6.34762475 23.6334765,6.35778095 C21.4905183,2.5898309 17.3975699,0.0609371976 12.7358743,0.0609371976 C5.88043957,0.0609371976 0.25390499,5.59606598 0.25390499,12.3905635 C0.25390499,14.706177 0.893745564,16.9405409 2.13280192,18.9108436 L0.0812495968,23.5319145 C-0.0203123992,23.7756633 0.0101561996,24.0905054 0.223436391,24.2631608 C0.396091784,24.476441 0.721090171,24.5475344 0.964838962,24.4358162 L6.02262636,22.7905119 C7.84058609,23.9280062 9.89213841,24.578003 12.0350965,24.6998774 C11.0093204,22.9022301 10.4202608,20.8303654 10.4202608,18.6264701 C10.4202608,11.8015039 16.0467954,6.33746855 22.9022301,6.33746855 Z"
                :fill="options.textColour"
              ></path>
              <path
                id="Shape"
                d="M32.1545279,24.4459724 C33.2412413,22.7194185 33.7998323,20.7491158 33.7998323,18.7077197 C33.7998323,12.9389983 29.1990738,8.20620927 23.4608211,7.87105469 C23.2475409,7.86089849 23.0342607,7.85074229 22.8108243,7.85074229 C16.7678855,7.85074229 11.8218163,12.6647809 11.8218163,18.6772511 C11.8218163,20.6170852 12.3397825,22.4452011 13.2436843,24.019412 C15.1327374,27.3303331 18.7178759,29.5545408 22.8108243,29.5545408 C24.9233138,29.5545408 26.9545537,28.9654812 28.7217325,27.8584555 L33.1701479,29.310792 C33.3834281,29.4021978 33.6678017,29.3412606 33.8201447,29.158449 C34.0029562,29.006106 34.0334248,28.7217325 33.9420191,28.5084523 L32.1545279,24.4459724 Z"
                :fill="options.textColour"
              ></path>
            </g>
          </g>
        </g>
      </svg>
      <span v-if="notificationCount" :key="notificationCount" class="dacb-notifications-count">{{ notificationCount }}</span>
    </span>
    <transition name="dacb-slidedown">
      <chat-window v-if="windowOpen" @close="close"/>
    </transition>
  </div>
</template>

<script>
import "@/styles/application.scss";

import * as Sentry from "@sentry/vue";
import ChatWindow from "components/ChatWindow.vue";
import Auth from "helpers/auth";
import bus from "helpers/bus";
import logger from "helpers/logger";
import {Howl} from "howler";
import Pusher from "pusher-js";
import _ from "underscore";
import {mapState} from "vuex";

import InitialMessagePop from "@/lib/InitialMessagePop";

export default {
  name: "App",

  components: {ChatWindow},

  props: {
    options: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      pusher: null,
      windowOpen: false,
      connectionError: false,
      connectionReties: 0,
      notificationCount: 0,
      audio: new Howl({src: ["https://app.ripley.chat/static/click.mp3"]}),
      pageTitle: "",
      messagesFetched: false,
      mobileClassEnabled: false,
      scrollPosition: null,
      safariIOS: false,
      inputFocused: false,
      lastMessageId: null
    };
  },

  computed: {
    ...mapState({
      conversation: (state) => state.conversation.current,
      messages: (state) => state.messages.all,
      settings: (state) => state.settings.settings,
      mobileMode: (state) => state.settings.mobileMode,
      customer: (state) => state.customer.current,
      robot: (state) => state.settings.robot
    })
  },

  watch: {
    conversation() {
      // Check if safari ios
      const userAgent = navigator.userAgent;
      const isSafari = userAgent.indexOf("Safari") > -1;
      const isMobile = userAgent.indexOf("Mobile") > -1;
      this.safariIOS = isSafari && isMobile;
    },

    messages() {
      if (this.messagesFetched) this.messageAppended();

      if (this.messagesFetched && !this.windowOpen) {
        // Calculate actual unread count instead of just incrementing
        this.notificationCount = this.unreadMessages().length;
        this.updatePageTitle();
      }

      if (this.windowOpen) this.markMessagesAsRead();
    }
  },

  mounted() {
    if (this.options.embedded) {
      this.enableMobileMode();
      this.open();
    }
  },

  created() {
    console.log("[RIPLEY WIDGET] Component created");
    bus.$on("connectionError", this.connectionFailed);
    bus.$on("connectPusher", () => {
      this.connectPusher();
    });
    bus.$on("inputFocus", () => {
      this.inputFocused = true;
    });
    bus.$on("inputBlur", () => {
      this.inputFocused = false;
      if (this.safariIOS) this.resetScrollPosition();
    });
    bus.$on("openChat", () => {
      this.open();
    });
    bus.$on("close", (val) => {
      this.close();
    });

    this.init();
  },

  unmounted() {
    // Clean up event listeners
    bus.$off("connectionError", this.connectionFailed);
    bus.$off("connectPusher");
    bus.$off("inputFocus");
    bus.$off("inputBlur");
    bus.$off("openChat");
    bus.$off("close");
  },

  methods: {
    init() {
      console.log("[RIPLEY WIDGET] Init started");
      this.pageTitle = document.title;

      this.$store.dispatch("setOptions", this.options);
      
      // Restore persisted conversation and customer state early
      this.$store.dispatch("conversation/restoreFromStorage");
      this.$store.dispatch("customer/restoreFromStorage");
      
      this.$store.dispatch("checkRobot");

      console.log("[RIPLEY WIDGET] Robot check:", this.robot);
      logger.info("check robot", this.robot);
      if (!this.robot) {
        console.log("[RIPLEY WIDGET] Not a robot, proceeding");
        logger.info("widget enabled not robot");

        this.$store.dispatch("fetchSettings").then(() => {
        if (!this.settings.widget_enabled) return;

        if (this.settings.campaign_prefix) {
          const campaign = this.settings.campaign_prefix;
          this.$store.dispatch("setCampaign", campaign);
        }

        this.$store.dispatch("setCookies");
        this.$store.dispatch("fetchOrCreateCustomer", (token) => {
          this.$store.dispatch("fetchOrCreateConversation").then(() => {
            this.messagesFetched = true;
            this.notificationCount = this.unreadMessages().length;
            this.updatePageTitle();
            this.setLastMessageId();

            // Connect to pusher if the user has previously sent messages
            if (
              _.find(this.messages, (m) => {
                return !_.isNull(m.attributes.customer_id);
              })
            ) {
              this.connectPusher();
            }
          });
        });
      });
      }
    },

    close() {
      this.disableMobileMode();
      this.windowOpen = false;
    },

    open() {
      if (window.innerWidth <= 450) this.enableMobileMode();

      this.windowOpen = true;

      if (this.notificationCount) {
        this.markMessagesAsRead();
      }
    },

    connectPusher() {
      if (this.pusher) return;

      logger.info("connect pusher");

      this.pusher = new Pusher(process.env.PUSHER_KEY, {
        authEndpoint: process.env.API_BASE_URL + "pusher/auth",
        auth: {
          headers: {Authorization: "Token " + Auth.token()}
        },
        encrypted: true,
        cluster: "eu"
      });

      const convoChannel = this.pusher.subscribe(`private-conversation-${this.conversation.id}`);
      const customerChannel = this.pusher.subscribe(`private-customer-${this.customer.id}`);

      convoChannel.bind("new-message", (data) => {
        logger.info("new-message", data.data);
        this.$store.dispatch("addMessage", {message: data.data, queue: true});
      });

      customerChannel.bind("clear-data", (data) => {
        logger.info("clear-data", data.data);
        this.pusher = null; // there will be a new token for the customer
        Auth.reset(data.data);
        this.$store.dispatch("reset", {customer: data.data});
      });
    },

    connectionFailed(err, message) {
      logger.info("Connection failed:", message, err);
      this.connectionError = true;

      if (err.response && err.response.status === 401 && this.connectionReties < 1) {
        // Authentication error, clear token and force new customer
        this.connectionReties++;
        Auth.clearToken();
        this.open();
      } else {
        // Send to sentry
        Sentry.captureException(err);
      }
    },

    updatePageTitle() {
      let title = this.pageTitle;

      if (this.notificationCount > 0) {
        title = "(" + this.notificationCount + ") " + title;
      }

      document.title = title;
    },

    unreadMessages() {
      console.log('unreadMessages');
      return _.select(this.messages, (msg) => {
        return !msg.attributes.customer_id && !msg.attributes.read_at;
      });
    },

    markMessagesAsRead() {
      _.each(this.unreadMessages(), (msg) => {
        this.$store.dispatch("messageRead", msg.id);
      });

      this.notificationCount = 0;
      this.updatePageTitle();
    },

    enableMobileMode() {
      if (this.mobileMode) return;
      this.$store.dispatch("setMobileMode", true);

      // Store current scroll position
      this.scrollPosition = window.pageYOffset;

      if (this.safariIOS) {
        // Set body height to 100% and remove overflow
        this.defaultBodyHeight = document.body.style.height || "auto";
        document.body.style.height = window.innerHeight - 44 + "px";
        this.defaultBodyOverflow = document.body.style.overflow || "visible";
        document.body.style.overflow = "hidden";
        this.defaultHtmlOverflow = document.getElementsByTagName("html")[0].style.overflow || "visible";
        document.getElementsByTagName("html")[0].style.overflow = "hidden";
      }

      // ensure viewport scaling is disabled
      const meta = document.createElement("meta");
      meta.name = "viewport";
      meta.content = "width=device-width, initial-scale=1, maximum-scale=1, user-scalable=0";
      meta.id = "dacb-vp";
      document.getElementsByTagName("head")[0].appendChild(meta);

      // Set the mobile class
      this.mobileClassEnabled = true;
      this.resetScrollPosition();
    },

    disableMobileMode() {
      if (!this.mobileMode) return;
      this.$store.dispatch("setMobileMode", false);

      // remove the vp meta tag
      const el = document.getElementById("dacb-vp");
      el.parentNode.removeChild(el);

      if (this.safariIOS) {
        // Reset height and overflow on page
        document.body.style.height = this.defaultBodyHeight;
        document.body.style.overflow = this.defaultBodyOverflow;
        document.getElementsByTagName("html")[0].style.overflow = this.defaultHtmlOverflow;
      }

      // Remove the mobile class
      this.mobileClassEnabled = false;

      // Scroll back to original position
      if (this.scrollPosition !== null) {
        this.resetScrollPosition();
        this.scrollPosition = null;
      }
    },

    resetScrollPosition() {
      const pos = this.mobileClassEnabled ? 0 : this.scrollPosition;
      window.scrollTo(0, pos);
    },

    setLastMessageId() {
      const lastMsg = _.last(this.messages);
      this.lastMessageId = lastMsg ? lastMsg.id : null;
    },

    messageAppended() {
      const msg = _.last(this.messages);

      // do nothing if simply updating an existing message (mark as read etc)
      if (this.lastMessageId === msg.id) return;
      this.setLastMessageId();

      // if it is the initial message, check how many times the user has
      // received the pop noise within the last 24 hours
      if (msg.isInitialMessage) {
        const maxPops = parseInt(this.settings.initial_message_max_pops, 10);
        if (InitialMessagePop.isDue(maxPops)) this.audio.play();

        // Standard message, always pop
      } else {
        this.audio.play();
      }
    }
  }
};
</script>
